// ============================================================
// Dashboard shared bits — real-data edition (Phase 2R)
// ============================================================
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type ApiProject, type ApiStats } from "../../api/http";
import { Chip, Icon, portalRoot, type ChipTone } from "../../components/ui";
import type { Lang } from "../../lib/i18n";

export const STATUS: Record<string, { tone: ChipTone; de: string; en: string }> = {
  active: { tone: "good", de: "Aktiv", en: "Active" },
  paused: { tone: "warn", de: "Pausiert", en: "Paused" },
  archived: { tone: "neutral", de: "Archiviert", en: "Archived" },
  pending: { tone: "teal", de: "Ausstehend", en: "Pending" },
  // legacy fallback
  maintenance: { tone: "warn", de: "Wartung", en: "Maintenance" },
};

export function nf(n: number, d = 0): string {
  return Number(n).toLocaleString("de-DE", { minimumFractionDigits: d, maximumFractionDigits: d });
}

/**
 * Breiten, die der Server fuer Projektbilder rechnet. Alles andere weist er ab —
 * dieselbe Abmachung wie bei photoUrl() in lib/format.ts.
 */
export type ThumbBreite = 160 | 320 | 640 | 1280;

/**
 * Adresse des Projektbildes.
 *
 * Vorher lag das Bild als base64-JPEG IN der Projektantwort: /api/projects wog
 * damit 985 360 B statt 1 372 B, und dasselbe Bild kam mit /api/projects/:id
 * gleich ein zweites Mal. Als eigene Datei laedt der Browser es nebenher,
 * verkleinert auf die Breite, die die Kachel wirklich zeigt.
 *
 * `v` haengt den Inhaltsstempel des Bildes an (`thumbSig` aus der
 * Projektantwort): NUR mit ihm liefert der Server `immutable` aus — ohne ihn
 * bliebe die Adresse beim Bildtausch dieselbe und der Browser zeigte weiter
 * das alte Bild.
 */
export function thumbUrl(projektId: string, w: ThumbBreite = 320, v?: string | number | null): string {
  return `/api/projects/${encodeURIComponent(projektId)}/thumb?w=${w}${v != null && v !== "" ? `&v=${encodeURIComponent(String(v))}` : ""}`;
}

/** Dichtevarianten fuer ein Bild: `srcSet` mit 1x/2x aus den erlaubten Breiten. */
export function thumbSrcSet(projektId: string, einfach: ThumbBreite, doppelt: ThumbBreite, v?: string | number | null): string {
  return `${thumbUrl(projektId, einfach, v)} 1x, ${thumbUrl(projektId, doppelt, v)} 2x`;
}

/**
 * Bildquelle einer Projektzeile — null heisst „sicher kein Bild".
 *
 * Drei Faelle, und der dritte ist der wichtige: `thumb === undefined` liefert
 * ein aelterer Server, der das Feld noch nicht kennt. Dann wird das Bild
 * ANGEFRAGT statt die Kachel leer zu lassen; hat das Projekt keines, antwortet
 * der Server mit 404 und ThumbFill zeigt seinen Platzhalter. Eine Kachel darf
 * an dieser Umstellung nicht verschwinden.
 */
export function thumbQuelle(p: Pick<ApiProject, "id" | "thumb" | "thumbUrl" | "thumbSig">, w: ThumbBreite = 320): string | null {
  if (p.thumbUrl) return p.thumbUrl; // alter Server: Bild steckt als data:-URL in der Antwort
  return p.thumb === false ? null : thumbUrl(p.id, w, p.thumbSig);
}

/**
 * Fills a box with a thumbnail of any aspect: the sharp image is shown whole
 * (contain), backed by a blurred, zoomed copy that covers the gaps — so square
 * or portrait sources never get cropped and the box never shows flat bars.
 *
 * Der Rahmen darum reserviert den Platz IMMER (feste Hoehe bzw. aspect-ratio),
 * deshalb springt beim Eintreffen des Bildes nichts; wo die Masse in Pixeln
 * feststehen (Listenzeile), gehen sie zusaetzlich als width/height ans Bild.
 * `fallback` steht bereit, weil ein 404 ausdruecklich vorgesehen ist: der
 * Server antwortet so, wenn das Projekt gar kein Bild hat.
 */
export function ThumbFill({
  src,
  srcSet,
  alt,
  radius = 0,
  posX = 50,
  breite,
  hoehe,
  fallback,
  onFehler,
}: {
  src: string;
  /** 1x/2x-Varianten — siehe thumbSrcSet() */
  srcSet?: string;
  alt?: string;
  radius?: number;
  posX?: number;
  breite?: number;
  hoehe?: number;
  /** was an der Stelle des Bildes steht, wenn es nicht geladen werden kann (404 = kein Bild) */
  fallback?: ReactNode;
  onFehler?: () => void;
}) {
  // the sharp (contain) image is nudged horizontally (0–100%); the blurred backdrop stays centered to always fill
  const objectPosition = `${Math.max(0, Math.min(100, posX))}% center`;
  const [fehler, setFehler] = useState(false);
  // neue Quelle = neuer Versuch (Bild ersetzt, Projekt gewechselt)
  useEffect(() => setFehler(false), [src, srcSet]);
  if (fehler) return <div style={{ position: "absolute", inset: 0, overflow: "hidden", borderRadius: radius }}>{fallback}</div>;
  return (
    <div style={{ position: "absolute", inset: 0, overflow: "hidden", borderRadius: radius }}>
      <img
        src={src}
        srcSet={srcSet}
        alt=""
        aria-hidden
        loading="lazy"
        decoding="async"
        width={breite}
        height={hoehe}
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", filter: "blur(20px) saturate(1.15)", transform: "scale(1.15)" }}
      />
      <img
        src={src}
        srcSet={srcSet}
        alt={alt ?? ""}
        loading="lazy"
        decoding="async"
        width={breite}
        height={hoehe}
        onError={() => {
          setFehler(true);
          onFehler?.();
        }}
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain", objectPosition }}
      />
    </div>
  );
}

export function StatusBadge({ status, lang }: { status: string; lang: Lang }) {
  const s = STATUS[status] ?? STATUS.active!;
  // the live "active" status gets a prominent accent ring + gently pulsing dot
  return (
    <Chip tone={s.tone} icon="circle" className={status === "active" ? "chip-live" : ""}>
      {s[lang]}
    </Chip>
  );
}

/**
 * Klick in ein Popup, das ueber dem Panel liegt? Select rendert seine Liste per
 * Portal an den Wurzelknoten — fuer den Aussen-Klick-Handler liegt sie also
 * ausserhalb. Ohne diese Ausnahme schloss der mousedown das Panel, die Liste
 * verschwand mit ihm und der Klick auf die Option kam nie an: genau der Grund,
 * warum sich im Filter kein Feld auswaehlen liess.
 */
function inStackedPopup(target: EventTarget | null): boolean {
  const el = target instanceof Element ? target : null;
  // Beides liegt im Portal UEBER dem Panel und darf es nicht schliessen: die
  // Auswahlliste eines Select — und ein Dialog, den das Panel selbst geoeffnet
  // hat (etwa die Rueckfrage vor dem Loeschen eines Lesezeichens). Ohne den
  // zweiten Fall stand man nach einem Abbrechen vor einem zugeklappten Menue.
  return !!el?.closest('[role="listbox"], .modal-wrap');
}

/** Steht gerade irgendein Dialog offen? Dann gehoert ihm die Escape-Taste. */
function modalOffen(): boolean {
  return !!document.querySelector(".modal-wrap");
}

/**
 * Kleines Dropdown-Panel — schliesst beim Klick nach aussen. Filterleiste und
 * Objektliste teilen sich dieselbe Mechanik, deshalb wohnt es hier und nicht
 * in einer der beiden Dateien.
 */
export function Menu({
  open,
  onClose,
  width = 260,
  align = "right",
  children,
}: {
  open: boolean;
  onClose: () => void;
  width?: number;
  /** an welcher Kante des Ausloesers das Panel haengt (Filterchips: links) */
  align?: "left" | "right";
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  /**
   * Randkorrektur. Das Panel haengt an einer Kante des Ausloesers — bei
   * align="right" oeffnet es nach LINKS. Steht der Ausloeser auf einem
   * schmalen Schirm weit links (die Spaltenwahl der Objektliste tut das),
   * landet die linke Kante im Negativen und die halbe Liste steht ausserhalb
   * des Bildes. `maxWidth` allein hilft nicht: das begrenzt die Breite, nicht
   * die Position. Also nach dem Oeffnen messen und nur so weit schieben, wie
   * noetig ist — auf dem Desktop ist die Verschiebung immer 0.
   */
  const [schub, setSchub] = useState(0);
  useLayoutEffect(() => {
    if (!open) {
      setSchub(0);
      return;
    }
    const el = ref.current;
    if (!el) return;
    const messen = () => {
      const r = el.getBoundingClientRect();
      // Ohne den bereits gesetzten Schub rechnen, sonst schaukelt sich die
      // Korrektur ueber mehrere Durchlaeufe auf.
      const links = r.left - schub;
      const rechts = r.right - schub;
      const rand = 8;
      let d = 0;
      if (links < rand) d = rand - links;
      else if (rechts > window.innerWidth - rand) d = window.innerWidth - rand - rechts;
      // Ein Panel, das breiter als der Schirm ist, wird an den linken Rand
      // gelegt statt hin- und hergeschoben.
      if (rechts - links > window.innerWidth - 2 * rand) d = rand - links;
      if (Math.abs(d - schub) > 0.5) setSchub(d);
    };
    messen();
    window.addEventListener("resize", messen);
    return () => window.removeEventListener("resize", messen);
  }, [open, schub]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (inStackedPopup(e.target)) return;
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div
      ref={ref}
      className="anim-pop"
      style={{
        position: "absolute",
        left: align === "left" ? 0 : undefined,
        right: align === "right" ? 0 : undefined,
        top: "calc(100% + 6px)",
        width,
        maxWidth: "calc(100vw - 16px)",
        transform: schub ? `translateX(${schub}px)` : undefined,
        borderRadius: 13,
        padding: 8,
        zIndex: 60,
        background: "var(--panel-solid)",
        border: "1px solid var(--glass-border-strong)",
        boxShadow: "var(--shadow-lg)",
      }}
    >
      {children}
    </div>
  );
}

/** Optik des Filterchips — gesetzter Filter faerbt ihn im Akzent. */
export function chipStyle(on: boolean, dashed?: boolean): CSSProperties {
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    fontSize: 12.5,
    fontWeight: 600,
    padding: "8px 13px",
    borderRadius: 11,
    cursor: "pointer",
    whiteSpace: "nowrap",
    flex: "none",
    border: dashed ? "1px dashed" : "1px solid",
    borderColor: on ? "var(--accent)" : "var(--field-border)",
    background: on ? "var(--accent-soft)" : "var(--field)",
    color: on ? "var(--accent-text)" : "var(--text-dim)",
    fontFamily: "inherit",
    transition: "all .15s ease",
  };
}

/**
 * Filterchip mit Aufklapp-Panel. Das Panel haengt per Portal am Wurzelknoten und
 * wird an der Chipposition ausgerichtet (Muster: Select): die Filterleiste
 * scrollt waagerecht, ein absolut positioniertes Panel wuerde darin abgeschnitten.
 */
export function Dropdown({
  open,
  onToggle,
  onClose,
  label,
  active,
  dashed,
  width = 280,
  title,
  children,
}: {
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  label: ReactNode;
  active?: boolean;
  dashed?: boolean;
  width?: number;
  title?: string;
  children: ReactNode;
}) {
  const btnRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  const place = useCallback(() => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    // am rechten Rand nach innen ruecken, damit das Panel nie aus dem Fenster laeuft
    setPos({ left: Math.max(8, Math.min(r.left, window.innerWidth - width - 8)), top: r.bottom + 6 });
  }, [width]);

  useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (panelRef.current?.contains(e.target as Node) || btnRef.current?.contains(e.target as Node)) return;
      // Select-Liste liegt im Portal ueber dem Panel — sie darf es nicht schliessen
      if (inStackedPopup(e.target)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      // Der Dialog schliesst sich bei Escape selbst; wuerde das Panel es auch
      // tun, verschwaende ein Tastendruck beides auf einmal.
      if (e.key === "Escape" && !modalOffen()) onClose();
    };
    const onMove = () => place();
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onMove);
    window.addEventListener("scroll", onMove, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onMove);
      window.removeEventListener("scroll", onMove, true);
    };
  }, [open, onClose, place]);

  return (
    <>
      {/* .filt-chip: auf dem Telefon greift darueber eine engere Dichte (base.css) */}
      <button ref={btnRef} type="button" className="filt-chip" onClick={onToggle} title={title} style={chipStyle(!!active, dashed)}>
        {label}
        <Icon name="caret-down" size={12} style={{ transition: "transform .15s", transform: open ? "rotate(180deg)" : "none" }} />
      </button>
      {open &&
        pos &&
        createPortal(
          <div
            ref={panelRef}
            className="anim-pop"
            style={{
              position: "fixed",
              left: pos.left,
              top: pos.top,
              width,
              maxWidth: "calc(100vw - 16px)",
              maxHeight: "min(70vh, 460px)",
              overflowY: "auto",
              borderRadius: 13,
              padding: 8,
              zIndex: 1000,
              background: "var(--panel-solid)",
              border: "1px solid var(--glass-border-strong)",
              boxShadow: "var(--shadow-lg)",
            }}
          >
            {children}
          </div>,
          portalRoot(),
        )}
    </>
  );
}

export function useProjects() {
  return useQuery({ queryKey: ["projects"], queryFn: () => api.projects.list() });
}

/**
 * Der Listeneintrag als „Schatten" der Detailzeile.
 *
 * Er ist Feld fuer Feld dieselbe Zeile — pub() im Backend baut beide Antworten
 * aus demselben Spaltensatz — mit EINER Luecke: `myRole` haengt der Server nur
 * dort an, wo der Projektwaechter die Rolle aufgeloest hat, also erst bei
 * GET /projects/:id. Wer aus einem Schatten eine Rolle liest, liest deshalb
 * `undefined`. Das ist nach unten sicher (keine Rolle = keine Knoepfe, und
 * verbindlich entscheidet ohnehin der ProjectAccessGuard), aber es ist
 * trotzdem eine Luecke: Rollenabhaengige Oberflaeche gehoert an
 * useProjectRole(), nicht an die Daten dieses Hooks.
 */
function listenSchatten(qc: ReturnType<typeof useQueryClient>, id: string | undefined): ApiProject | undefined {
  if (!id) return undefined;
  return qc.getQueryData<ApiProject[]>(["projects"])?.find((p) => p.id === id);
}

/**
 * Ein Projekt — mit Sofort-Schicht aus der bereits geladenen Liste.
 *
 * Die Liste (`['projects']`) traegt dieselbe Zeile: Name, Beschreibung,
 * Kachelkennzahlen, Bild. Sie sofort zu zeigen, spart dem Dashboard die erste
 * serielle Wartestufe — der Inhalt steht, waehrend die Einzelantwort laeuft.
 *
 * placeholderData und AUSDRUECKLICH nicht initialData — nachgespielt am
 * 27.08.2026, drei Punkte, und alle drei haengen daran:
 *
 * 1. Der Schatten hat kein `myRole` (siehe listenSchatten). placeholderData
 *    bleibt im BEOBACHTER: er faerbt den Cache nicht ein. Am selben Schluessel
 *    `['project', id]` haengen aber drei fremde Beobachter, die `myRole` direkt
 *    lesen — useProjectRole.ts:50, KarteScreen.tsx:765, ChatWidget.tsx:275.
 *    Mit initialData bekaemen die drei die lueckenhafte Zeile aus dem CACHE:
 *    eine Editorin saehe auf der Karte die Oberflaeche einer Nichtmitgliedin.
 *    Mit placeholderData sehen sie davon nichts.
 * 2. Aufgefrischt wird in jedem Fall: ohne Eintrag im Cache laeuft die Abfrage
 *    los, staleTime spielt gar nicht erst mit. initialData braeuchte dafuer
 *    zusaetzlich `initialDataUpdatedAt: 0` — ohne das gaelte die Zeile als
 *    frisch, und useProjectRole (staleTime 30 s) haette sie eine halbe Minute
 *    lang festgehalten.
 * 3. Nach einer Absage verschwindet der Schatten von selbst: placeholderData
 *    greift nur, solange die Abfrage `pending` ist. Wird eine Mitgliedschaft in
 *    einem anderen Reiter entzogen und der Server antwortet 404, faellt `data`
 *    auf undefined zurueck. initialData haette die Zeile IM Cache gelassen —
 *    ein Projekt bliebe sichtbar, das der Server gerade verweigert hat.
 */
export function useProject(id: string | undefined) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: ["project", id],
    queryFn: () => api.projects.get(id!),
    enabled: !!id,
    placeholderData: () => listenSchatten(qc, id),
  });
}

export function useProjectStats(id: string | undefined) {
  return useQuery({ queryKey: ["stats", id], queryFn: () => api.projects.stats(id!), enabled: !!id });
}

/** Stats for a list of projects (cards + summary strip). */
export function useAllStats(projects: ApiProject[] | undefined) {
  return useQueries({
    queries: (projects ?? []).map((p) => ({
      queryKey: ["stats", p.id],
      queryFn: () => api.projects.stats(p.id),
      staleTime: 60_000,
    })),
  });
}

export type { ApiProject, ApiStats };

/**
 * Gesamt-Kennzahlen ueber alle zugaenglichen Projekte — Grundlage der schmalen
 * „Gesamt"-Zeile auf der Hauptseite (n Kommunen · Objekte · Flaeche). Nutzt
 * denselben Query-Key wie useAllStats/useProjectStats, kostet bei einem
 * einzigen Projekt also keine zusaetzliche Abfrage.
 */
export function useTotals(projects: ApiProject[] | undefined): { projects: number; count: number; area: number; loading: boolean } {
  const statsQ = useAllStats(projects);
  const n = projects?.length ?? 0;
  return {
    projects: n,
    count: statsQ.reduce((a, q) => a + (q.data?.count ?? 0), 0),
    area: statsQ.reduce((a, q) => a + (q.data?.totalAreaM2 ?? 0), 0),
    // solange einzelne Stats noch laufen: lieber „…" als eine irrefuehrende Summe
    loading: n > 0 && statsQ.some((q) => q.isLoading || q.data === undefined),
  };
}

/** Segment-Umschalter (Objekte|Ortsteile u. ae.) — EINE Komponente fuer
 *  Objektliste UND Diagramme, damit die Zwillinge nie auseinanderdriften. */
export function SegSwitch({ value, onChange, optionen, title }: { value: string; onChange: (v: string) => void; optionen: Array<{ id: string; label: string }>; title?: string }) {
  return (
    <div className="seg" role="tablist" title={title} style={{ display: "inline-flex", alignItems: "stretch", border: "1px solid var(--glass-border-strong)", borderRadius: 10, overflow: "hidden", height: 36 }}>
      {optionen.map((o) => (
        <button
          key={o.id}
          role="tab"
          aria-selected={value === o.id}
          onClick={() => onChange(o.id)}
          className="ring-focus"
          style={{ border: "none", cursor: "pointer", padding: "0 14px", fontSize: 12.5, fontWeight: 600, fontFamily: "inherit", background: value === o.id ? "var(--accent-soft)" : "transparent", color: value === o.id ? "var(--accent-text)" : "var(--text-mute)" }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
